import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Button, { BackButton } from 'components/Button';
import { H6 } from 'components/Typography';
import { TextInput, Select, CheckboxInput } from 'components/Inputs';
import { useDispatch, useSelector } from 'react-redux';
import history from 'routing/history';
import {
  BankTransferField,
  indexOfBankTransferField,
  getSupportedCountries,
  getKeyForField,
  getSupportedCurrenciesForCountryDropdown,
  getFieldsForCurrencyAndCountry,
  getWarningForCurrencyAndCountry
} from 'assets/data/currencies';
import ViewAnimator from '../../components/ViewAnimator';
import useNavigateToNextStep from '../../hooks/useNavigateToNextStep';
import { STEP_PATHS } from '../../consts';
import {
  editClaim,
  addValidStep,
  removeValidStep,
  initiateClaim,
  updateBankingDetails,
  getBankingDetails
} from '../../state';
import EditClaimError from '../../components/EditClaimError';
import {
  CurrencySelect,
  BankInfoHeading,
  StyledCountrySelect,
  BankInfo,
  LoadingOverlay,
  StyledFeedback
} from './styled';
import { StepHeading, NavigationButtons } from '../../styled';

const Reimbursement = () => {
  const dispatch = useDispatch();
  const { claimType } = useParams();
  const { claimID, editError, isLoading, error, bankAccount, contractID } = useSelector(
    (state) => state.nomadInsurance.claims
  );
  const [requiredFields, setRequiredFields] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState(null);
  const [bankCountry, setBankCountry] = useState(bankAccount?.bankCountry);
  const [reimbursementCurrency, setReimbursementCurrency] = useState(
    bankAccount?.reimbursementCurrency
  );
  const [bankWarning, setBankWarning] = useState(null);

  useNavigateToNextStep(
    `/nomad-insurance/claim/${claimType}/${STEP_PATHS.UPLOAD_DOCUMENTS}`,
    error || editError
  );

  useEffect(() => {
    if (!bankAccount) {
      dispatch(getBankingDetails());
    } else {
      setBankCountry(bankAccount.bankCountry);
      setReimbursementCurrency(bankAccount.reimbursementCurrency);
    }
  }, [bankAccount, dispatch]);

  const initialValues = useMemo(() => {
    /**
     * Since there are two form different fields that map to the same payload property
     * we need to determine which one to use. We are using a magic string
     * since we the value is stored in an enum and this file is still not typescript
     */
    const usesBankClearingCode =
      bankAccount?.reimbursementCurrency && bankAccount?.bankCountry
        ? getFieldsForCurrencyAndCountry(
            bankAccount.reimbursementCurrency,
            bankAccount.bankCountry
          ).filter((field) =>
            field.field.startsWith(BankTransferField.bankClearingCodeWhenNotIban)
          ).length > 0
        : false;

    return {
      id: bankAccount?.id ?? '',
      displayName: bankAccount?.displayName ?? '',
      fullNameOfAccountHolder: bankAccount?.fullNameOfAccountHolder ?? '',
      accountHolderPhoneNumber: bankAccount?.accountHolderPhoneNumber ?? '',
      accountNumberOrIban: bankAccount?.accountNumberOrIban ?? '',
      routingOrBankBranchCode: usesBankClearingCode
        ? bankAccount?.bankClearingCodeWhenNotIban ?? ''
        : bankAccount?.routingOrBankBranchCode ?? '',
      bankClearingCodeWhenNotIban: bankAccount?.bankClearingCodeWhenNotIban ?? '',
      swift: bankAccount?.swift ?? '',
      bankName: bankAccount?.bankName ?? '',
      bankCity: bankAccount?.bankCity ?? '',
      bankCountry: bankAccount?.bankCountry ?? '',
      reimbursementCurrency: bankAccount?.reimbursementCurrency ?? '',
      beneficiaryTaxId: bankAccount?.beneficiaryTaxId ?? '',
      beneficiaryNationalId: bankAccount?.beneficiaryNationalId ?? '',
      bankBranchName: bankAccount?.bankBranchName ?? '',
      bankCode: bankAccount?.bankCode ?? '',
      accountType: bankAccount?.accountType ?? '',
      indianFinancialSystemCode: bankAccount?.indianFinancialSystemCode ?? '',
      beneficiaryVO: bankAccount?.beneficiaryVO ?? '',
      includeVATax: bankAccount?.includeVATax ?? false,
      purposeOfPayment: bankAccount?.purposeOfPayment ?? '',
      vatAmount: bankAccount?.vatAmount ?? '',
      vatCode: bankAccount?.vatCode ?? '',
      beneficiaryPatronymic: bankAccount?.beneficiaryPatronymic ?? '',
      beneficiaryCPF: bankAccount?.beneficiaryCPF ?? '',
      beneficiaryKNP: bankAccount?.beneficiaryKNP ?? '',
      beneficiaryKBE: bankAccount?.beneficiaryKBE ?? '',
      agencyCode: bankAccount?.agencyCode ?? '',
      curpNumber: bankAccount?.curpNumber ?? ''
    };
  }, [bankAccount]);

  /**
   * Used so that if have bankDetails loaded from a different country
   * we don't mix their fields with the new country's fields
   */
  const filterBankingDetails = (info, requiredFields) => {
    const requiredKeys = new Set(
      requiredFields?.map((field) => getKeyForField(field.field)) || []
    );
    return Object.keys(info).reduce((acc, key) => {
      if (
        requiredKeys.has(key) ||
        key === 'bankCountry' ||
        key === 'reimbursementCurrency'
      ) {
        acc[key] = info[key];
      }
      return acc;
    }, {});
  };

  const generateTestsForField = (currentObject) => {
    return Yup.string().test(
      `${getKeyForField(currentObject.field)}-validation`,
      (value, context) => {
        if (currentObject.optional && (!value || value.length === 0)) {
          return true;
        }
        if (!value || value.length === 0) {
          return context.createError({ message: 'Required field.' });
        }
        if (!currentObject.validate(value)) {
          return context.createError({
            message: currentObject.validationErrorMessage
          });
        }
        return true;
      }
    );
  };

  const validationSchema = useMemo(() => {
    setBankWarning(getWarningForCurrencyAndCountry(reimbursementCurrency, bankCountry));

    if (!reimbursementCurrency || !bankCountry) {
      return Yup.object({});
    }

    const fields = getFieldsForCurrencyAndCountry(reimbursementCurrency, bankCountry);

    const validationObject = fields.reduce((acc, currentObject) => {
      acc[getKeyForField(currentObject.field)] = generateTestsForField(currentObject);
      return acc;
    }, {});

    return Yup.object()
      .shape(validationObject)
      .test(
        'bankName-or-swift',
        'Either bank name or swift code is required',
        function (value) {
          if (!(value.bankName || value.swift)) {
            // eslint-disable-next-line react/no-this-in-sfc
            return this.createError({
              path: 'eitherBankNameOrSwift',
              message: 'Either bank name or swift code is required'
            });
          }
          return true;
        }
      );
  }, [reimbursementCurrency, bankCountry]);

  const formik = useFormik({
    initialValues,
    onSubmit: async (info) => {
      const filteredInfo = filterBankingDetails(
        { ...info, bankCountry, reimbursementCurrency },
        requiredFields
      );

      await dispatch(
        updateBankingDetails({
          insuranceContractId: contractID.value,
          bankDetails: filteredInfo
        })
      );

      if (claimID) {
        await dispatch(editClaim({ claimID, editedInfo: filteredInfo }));
      } else {
        await dispatch(initiateClaim());
      }
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    validateOnChange: true
  });

  useEffect(() => {
    formik.setValues(initialValues);

    const newCurrencies =
      bankCountry && getSupportedCurrenciesForCountryDropdown(bankCountry);

    if (newCurrencies) {
      setAvailableCurrencies(newCurrencies);
    }
  }, [bankCountry]);

  useEffect(() => {
    if (formik.isValid) {
      dispatch(addValidStep(STEP_PATHS.REIMBURSEMENT));
    } else {
      dispatch(removeValidStep(STEP_PATHS.REIMBURSEMENT));
    }
  }, [formik.isValid, dispatch]);

  const addStarIfRequired = (label, optional) => `${label}${optional ? '' : '*'}`;

  /**
   * Input error messages only appear if the input has been touched but
   * if they were prefilled they won't be marked as touched. So force
   * a touch if the form was submitted so the field errors appear
   */
  useEffect(() => {
    if (requiredFields && requiredFields.length > 0) {
      const touchedFields = requiredFields.reduce((acc, field) => {
        acc[getKeyForField(field.field)] = true;
        return acc;
      }, {});

      formik.setTouched(touchedFields);
    }
  }, [bankCountry, reimbursementCurrency]);

  useEffect(() => {
    if (reimbursementCurrency && bankCountry) {
      const fields = getFieldsForCurrencyAndCountry(reimbursementCurrency, bankCountry);
      setRequiredFields(fields);
    }
  }, [bankCountry, reimbursementCurrency]);

  return (
    <ViewAnimator>
      <LoadingOverlay isLoading={isLoading} variant="light" />
      <StepHeading>Reimbursement</StepHeading>
      <H6>In which country does your bank reside?</H6>
      <StyledCountrySelect
        name="bankCountry"
        variant="outline"
        options={getSupportedCountries}
        value={bankCountry}
        onChange={(option) => {
          setBankCountry(option.value);
          setReimbursementCurrency(null);
        }}
      />
      {availableCurrencies && (
        <>
          <H6>In which currency would you like to be reimbursed?</H6>
          <CurrencySelect
            variant="outline"
            name="reimbursementCurrency"
            options={availableCurrencies}
            value={reimbursementCurrency}
            onChange={(option) => setReimbursementCurrency(option.value)}
          />
        </>
      )}
      {reimbursementCurrency && bankCountry && (
        <>
          <BankInfoHeading>Your bank information</BankInfoHeading>
          <StyledFeedback variant="warningOrange" topAlignIcon>
            Right now we can only support one bank account at a time. Changing your
            banking information now means that any reimbursement for this claim <i>and</i>{' '}
            any pending claims will be sent to that account.
          </StyledFeedback>
          {bankWarning &&
            (Array.isArray(bankWarning) ? (
              bankWarning.map((warning, _) => (
                <StyledFeedback variant="warningOrange" topAlignIcon>
                  {warning.text}
                </StyledFeedback>
              ))
            ) : (
              <StyledFeedback variant="warningOrange" topAlignIcon>
                {bankWarning.text}
              </StyledFeedback>
            ))}
          {formik.errors?.eitherBankNameOrSwift && (
            <StyledFeedback variant="warningOrange" topAlignIcon>
              {formik.errors.eitherBankNameOrSwift}
            </StyledFeedback>
          )}
          <BankInfo>
            {requiredFields?.map((bankRequirement) => {
              if (bankRequirement.field === BankTransferField.accountType) {
                return (
                  <Select
                    options={[
                      { value: 'CHECKING', label: 'Checking' },
                      { value: 'SAVINGS', label: 'Savings' }
                    ]}
                    formik={formik}
                    label={addStarIfRequired('Account Type', bankRequirement.optional)}
                    name={getKeyForField(bankRequirement.field)}
                    key={indexOfBankTransferField(bankRequirement.field)}
                  />
                );
              }
              if (!bankRequirement.type) {
                return (
                  <TextInput
                    label={addStarIfRequired(
                      bankRequirement.label || bankRequirement.field,
                      bankRequirement.optional
                    )}
                    formik={formik}
                    name={getKeyForField(bankRequirement.field)}
                    key={indexOfBankTransferField(bankRequirement.field)}
                    tooltip={bankRequirement?.tooltip}
                  />
                );
              }
              // TODO: Implement checkbox input
              if (bankRequirement.type === 'boolean') {
                return (
                  <CheckboxInput
                    label={addStarIfRequired(
                      bankRequirement.label || bankRequirement.field,
                      bankRequirement.optional
                    )}
                    formik={formik}
                    name={`bankDetails.${getKeyForField(bankRequirement.field)}`}
                    key={indexOfBankTransferField(bankRequirement.field)}
                    tooltip={bankRequirement?.tooltip}
                  />
                );
              }
              return null;
            })}
          </BankInfo>
        </>
      )}
      <EditClaimError
        error={error || editError}
        isVisible={Boolean(error || editError)}
      />
      <NavigationButtons>
        <BackButton
          onClick={() =>
            history.push(
              `/nomad-insurance/claim/${claimType}/${STEP_PATHS.PERSONAL_INFORMATION}`
            )
          }
          variant="dark"
        >
          Back
        </BackButton>
        <Button
          onClick={() => formik.submitForm()}
          disabled={
            !formik.isValid || isLoading || !reimbursementCurrency || !bankCountry
          }
        >
          Save & next
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default Reimbursement;
